import { useMemo } from 'react';

import { TenantInformationQuery } from '@/modules/tenant/graphql/TenantInformation.generated';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';

import {
  PresentationCoverSlideConfig,
  SlideConfigVariant,
} from '../coverSlide/CoverSlide.types';

export type SlideConfigFragment = NonNullable<
  TenantInformationQuery['coverSlideConfig']
>;

export interface MergeSlideDesignArgs {
  variant: SlideConfigVariant;
  /** Highest-priority override (e.g. live form values in the admin preview). */
  propOverride?: PresentationCoverSlideConfig;
  /** Tenant-default cover and divider configs from the TenantInformation query. */
  tenantCoverConfig?: SlideConfigFragment | null;
  tenantDividerConfig?: SlideConfigFragment | null;
  /** Sub-brand cover and divider configs when a sub-brand is active. */
  subBrandCoverConfig?: SlideConfigFragment | null;
  subBrandDividerConfig?: SlideConfigFragment | null;
  /**
   * True when a sub-brand is selected (editor/preview dropdown or
   * household association). When active, only sub-brand configs are
   * used — tenant-level configs are ignored so the slide falls back
   * to system defaults instead of leaking tenant values.
   */
  hasActiveSubBrand: boolean;
}

/**
 * Converts a GraphQL config fragment into the component-level
 * PresentationCoverSlideConfig shape. The BE stores backgroundAlpha
 * as a 0-1 float; the component interface uses 0-100 percentage.
 */
function toSlideDesign(
  config: SlideConfigFragment
): PresentationCoverSlideConfig {
  return {
    backgroundColor: config.backgroundColor,
    useDarkLogo: config.useDarkLogo,
    footerColor: config.footerColor,
    backgroundGradientEndColor: config.backgroundGradientEndColor,
    backgroundGradientAngle: config.backgroundGradientAngle,
    backgroundOverlayPath: config.backgroundOverlayPath,
    backgroundOverlayAlpha: config.backgroundOverlayAlpha,
    backgroundOverlayPercentScale: config.backgroundOverlayPercentScale,
    backgroundOverlayOriginX: config.backgroundOverlayOriginX,
    backgroundOverlayOriginY: config.backgroundOverlayOriginY,
    textColor: config.textColor,
    backgroundImagePath: config.backgroundImagePath,
    // Treat null/undefined as 100% (fully opaque); only 0 means
    // fully transparent when explicitly set.
    backgroundImageAlpha: Math.round((config.backgroundAlpha ?? 1) * 100),
    titleTextSize: config.titleTextSize,
    titleSizeRem: config.titleSizeRem ?? null,
    bodyTextColor: config.bodyTextColor,
    bodyTextAlignment: config.bodyTextAlignment ?? null,
    includeFooter: config.includeFooter ?? null,
    dividerInheritCover: config.dividerInheritCover,
    backgroundImageOriginX: config.backgroundImageOriginX ?? null,
    backgroundImageOriginY: config.backgroundImageOriginY ?? null,
    backgroundImagePercentScale: config.backgroundImagePercentScale ?? null,
    textOriginX: config.textOriginX ?? null,
    textOriginY: config.textOriginY ?? null,
    textWidth: config.textWidth ?? null,
    logoOriginX: config.logoOriginX ?? null,
    logoOriginY: config.logoOriginY ?? null,
    logoPercentScale: config.logoPercentScale ?? null,
    useDefaultLogoPosition: config.useDefaultLogoPosition ?? null,
    hideLogo: config.hideLogo ?? null,
  };
}

/** Returns true if the value is not null or undefined. */
function isPresent<T>(value: T | null | undefined): value is T {
  return value != null;
}

/**
 * Merges slide design config from multiple sources at the field level.
 *
 * When a sub-brand is active (`hasActiveSubBrand` is true), only the
 * sub-brand's configs are considered — tenant-level configs are never
 * used, so an absent sub-brand config produces system defaults rather
 * than leaking tenant values.
 *
 * ## Cover slide
 * Priority: prop override → effective cover config → component defaults.
 *
 * ## Divider slide
 * The effective divider config is the sub-brand's divider (when a
 * sub-brand is active) or the tenant's divider (otherwise). The
 * divider never implicitly falls back to the cover config in the
 * resolution chain — that path is reserved for the explicit
 * `dividerInheritCover` flag.
 *
 * The divider has a single `dividerInheritCover` flag (defaults to true):
 *   - **inherit = true**: use the cover slide's values for every field,
 *     ignoring any local divider config.
 *   - **inherit = false**: use the divider's own values; if a field is
 *     null, fall back to the component default (NOT the cover value).
 *
 * The prop override (admin preview) takes highest priority and carries
 * its own inherit flag from the form checkbox.
 *
 * Returns undefined when no design config exists from any source,
 * allowing the slide component to use its default styling.
 */
export function mergeSlideDesign({
  variant,
  propOverride,
  tenantCoverConfig,
  tenantDividerConfig,
  subBrandCoverConfig,
  subBrandDividerConfig,
  hasActiveSubBrand,
}: MergeSlideDesignArgs): PresentationCoverSlideConfig | undefined {
  // When a sub-brand is active, only use sub-brand configs; when no
  // sub-brand is active, only use tenant configs. This prevents
  // tenant values from leaking into a sub-brand's slides.
  const effectiveCover = hasActiveSubBrand
    ? subBrandCoverConfig
    : tenantCoverConfig;
  // The divider does NOT fall back to the cover config here; that
  // fallback is handled by the dividerInheritCover flag below.
  const effectiveDivider = hasActiveSubBrand
    ? subBrandDividerConfig
    : tenantDividerConfig;

  const coverActive = effectiveCover
    ? toSlideDesign(effectiveCover)
    : undefined;

  // --- Cover slide: prop override > active cover config > defaults ---
  if (variant === SlideConfigVariant.COVER) {
    const layers = [propOverride, propOverride ? undefined : coverActive];

    if (layers.every((l) => !l)) return undefined;

    function resolveField<K extends keyof PresentationCoverSlideConfig>(
      field: K
    ): PresentationCoverSlideConfig[K] {
      for (const layer of layers) {
        const value = layer?.[field];
        if (isPresent(value)) return value;
      }
      return null as PresentationCoverSlideConfig[K];
    }

    return {
      backgroundColor: resolveField('backgroundColor'),
      useDarkLogo: resolveField('useDarkLogo'),
      footerColor: resolveField('footerColor'),
      backgroundGradientEndColor: resolveField('backgroundGradientEndColor'),
      backgroundGradientAngle: resolveField('backgroundGradientAngle'),
      backgroundOverlayPath: resolveField('backgroundOverlayPath'),
      backgroundOverlayAlpha: resolveField('backgroundOverlayAlpha'),
      backgroundOverlayPercentScale: resolveField(
        'backgroundOverlayPercentScale'
      ),
      backgroundOverlayOriginX: resolveField('backgroundOverlayOriginX'),
      backgroundOverlayOriginY: resolveField('backgroundOverlayOriginY'),
      textColor: resolveField('textColor'),
      backgroundImagePath: resolveField('backgroundImagePath'),
      backgroundImageAlpha: resolveField('backgroundImageAlpha'),
      titleTextSize: resolveField('titleTextSize'),
      titleSizeRem: resolveField('titleSizeRem'),
      bodyTextColor: resolveField('bodyTextColor'),
      bodyTextAlignment: resolveField('bodyTextAlignment'),
      includeFooter: resolveField('includeFooter'),
      backgroundImageOriginX: resolveField('backgroundImageOriginX'),
      backgroundImageOriginY: resolveField('backgroundImageOriginY'),
      textOriginX: resolveField('textOriginX'),
      textOriginY: resolveField('textOriginY'),
      textWidth: resolveField('textWidth'),
      backgroundImagePercentScale: resolveField('backgroundImagePercentScale'),
      logoPercentScale: resolveField('logoPercentScale'),
      useDefaultLogoPosition: resolveField('useDefaultLogoPosition'),
      logoOriginX: resolveField('logoOriginX'),
      logoOriginY: resolveField('logoOriginY'),
      hideLogo: resolveField('hideLogo'),
    };
  }

  // --- Divider slide: single inherit flag ---
  const dividerActive = effectiveDivider
    ? toSlideDesign(effectiveDivider)
    : undefined;

  // The divider config source is either the prop override (admin
  // preview) or the saved divider config (sub-brand or tenant).
  const dividerSource = propOverride ?? dividerActive;
  const shouldInherit = dividerSource?.dividerInheritCover ?? true;

  // When inheriting, use the active cover slide's config directly.
  if (shouldInherit) {
    return coverActive;
  }

  // Otherwise use the divider's own config.
  if (!dividerSource) return undefined;
  return {
    backgroundColor: dividerSource.backgroundColor ?? null,
    useDarkLogo: dividerSource.useDarkLogo ?? null,
    footerColor: dividerSource.footerColor ?? null,
    backgroundGradientEndColor:
      dividerSource.backgroundGradientEndColor ?? null,
    backgroundGradientAngle: dividerSource.backgroundGradientAngle ?? null,
    backgroundOverlayPath: dividerSource.backgroundOverlayPath ?? null,
    backgroundOverlayAlpha: dividerSource.backgroundOverlayAlpha ?? null,
    backgroundOverlayPercentScale:
      dividerSource.backgroundOverlayPercentScale ?? null,
    backgroundOverlayOriginX: dividerSource.backgroundOverlayOriginX ?? null,
    backgroundOverlayOriginY: dividerSource.backgroundOverlayOriginY ?? null,
    textColor: dividerSource.textColor ?? null,
    backgroundImagePath: dividerSource.backgroundImagePath ?? null,
    backgroundImageAlpha: dividerSource.backgroundImageAlpha ?? null,
    titleTextSize: dividerSource.titleTextSize ?? null,
    titleSizeRem: dividerSource.titleSizeRem ?? null,
    bodyTextColor: dividerSource.bodyTextColor ?? null,
    bodyTextAlignment: dividerSource.bodyTextAlignment ?? null,
    includeFooter: dividerSource.includeFooter ?? null,
    backgroundImageOriginX: dividerSource.backgroundImageOriginX ?? null,
    backgroundImageOriginY: dividerSource.backgroundImageOriginY ?? null,
    textOriginX: dividerSource.textOriginX ?? null,
    textOriginY: dividerSource.textOriginY ?? null,
    textWidth: dividerSource.textWidth ?? null,
    backgroundImagePercentScale:
      dividerSource.backgroundImagePercentScale ?? null,
    logoPercentScale: dividerSource.logoPercentScale ?? null,
    useDefaultLogoPosition: dividerSource.useDefaultLogoPosition ?? null,
    logoOriginX: dividerSource.logoOriginX ?? null,
    logoOriginY: dividerSource.logoOriginY ?? null,
    hideLogo: dividerSource.hideLogo ?? null,
  };
}

/**
 * Hook wrapper around `mergeSlideDesign` that gathers the tenant context.
 * See `mergeSlideDesign` for merge semantics.
 */
export function useMergedSlideDesign(
  variant: SlideConfigVariant,
  propOverride?: PresentationCoverSlideConfig
): PresentationCoverSlideConfig | undefined {
  const {
    coverSlideConfig,
    dividerSlideConfig,
    activeSubBrandId,
    subBrandsById,
  } = useTenantDetailsContext();

  const activeSubBrand = activeSubBrandId
    ? subBrandsById[activeSubBrandId]
    : undefined;

  return useMemo(
    () =>
      mergeSlideDesign({
        variant,
        propOverride,
        tenantCoverConfig: coverSlideConfig,
        tenantDividerConfig: dividerSlideConfig,
        subBrandCoverConfig: activeSubBrand?.presentationCoverSlideConfig,
        subBrandDividerConfig: activeSubBrand?.presentationDividerSlideConfig,
        hasActiveSubBrand: !!activeSubBrandId,
      }),
    [
      variant,
      propOverride,
      coverSlideConfig,
      dividerSlideConfig,
      activeSubBrand,
      activeSubBrandId,
    ]
  );
}
